/**
 * Hooks to simplify usage of managed and validated forms.
 *
 * @packageDocumentation
 */
import type { ChangeEvent } from 'react';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import type { FormikConfig } from 'formik';
import { useFormik } from 'formik';
import type { Scale, VoidCallback } from '../../components';
import { Button } from '../../components';
import { intl } from '..';
import type { ObjectSchema } from 'yup';
import _ from 'lodash';
import isEqual from 'react-fast-compare';
import type { FormikErrors, FormikValues } from 'formik/dist/types';

// eslint-disable-next-line @typescript-eslint/ban-types
export type FormErrors<T extends object> = {
  [Prop in keyof T]?: string | undefined;
};

// eslint-disable-next-line @typescript-eslint/ban-types
export type FormTouched<T extends object> = { [Prop in keyof T]: boolean };

/**
 * Configuration for a managed form.
 *
 * It can either be validated directly, or through a Yup schema (which is type-checked!).
 *
 */
// eslint-disable-next-line @typescript-eslint/ban-types
export interface UseFormParam<T extends object>
  extends Omit<
    FormikConfig<T>,
    | 'children'
    | 'component'
    | 'render'
    | 'onSubmit'
    | 'validationSchema'
    | 'validate'
  > {
  /**
   * Whether to auto-submit the form on every valid change.
   * If you autosubmit using an ajax request, you should use `createRequestCancellingPrevious` for it!
   *
   * If disabled, onSubmit is only called when a submit button is used (or on enter pressed).
   * If enabled, onSubmit will be called on every change, debounced (by default with 500ms) - this allows e.g. dynamic filter panels.
   * Of course, validation is applied first if it is configured.
   *
   * The debounce interval (in ms) can be specified as well.
   *
   * Note: When using this, you might want to add a hidden button using {@link SubmitButtonOptions#hideButKeepForSubmitOnEnter} set to true
   * to enable submitting on pressing Enter.
   */
  autoSubmitWhenValid?: boolean | number;
  /**
   * A yup schema to use for form validation.
   *
   * You can manually specify error messages in the yup schema!
   */
  validationSchema?: ObjectSchema<T>;
  /**
   * A manual validation function.
   *
   * It should return an object mapping form fields to a string describing an error.
   * To make this easier, it is passed an empty errors object for autocompletion :)
   */
  validate?: (
    values: T,
    errors: FormErrors<T>
  ) => FormErrors<T> | Promise<FormErrors<T>>;
  /**
   * Handler that gets called when the form is submitted.
   *
   * This can be a sync or async function.
   * If it is not async, you *must* call `submissionFinished` when you are done to let formik know that the submission finished,
   * because no other submission will be possible until this is called.
   *
   * @param values The current state of the form
   * @param submissionFinished A callback to let formik know that a synchronous (blocking) form submission is finished.
   * @param resetForm A callback to reset the form to it's initial state.
   */
  onSubmit: (
    values: T,
    submissionFinished: () => void,
    resetForm: () => void
  ) => void | Promise<void>;

  /**
   * On true, the field with an error will be scrolled into view (at the top of the form viewport).
   * So it works, formId should also be provided.
   */
  scrollErrorIntoView?: boolean;
  /**
   * This parameter is needed for:
   * - scrolling error into view, so not to mix with different form
   * - submit button, if the it is outside the <form> element
   */
  formId?: string;
}

/**
 * All properties managed by the {@link useManagedForm} hook.
 *
 * Spread this over the input field component to make it managed.
 * TODO: also allow nested properties
 *
 */
// eslint-disable-next-line @typescript-eslint/ban-types
export interface ManagedFormFieldProps<T extends object, Name extends keyof T> {
  /** Change handler to register the input with the managed form. */
  onChange: (value: T[Name]) => void;
  /** Blur handler for touched tracking. */
  onBlur: VoidCallback;
  /** The value in the managed state. */
  value: T[Name];
  /** error message | undefined depending on whether the field is valid. */
  error: FormErrors<T>[Name];

  /** The state property used for this input, in case we want to use it as name. */
  name: Name;
  /** Whether the input was touched. */
  touched: FormTouched<T>[Name];
}

/**
 * Properties to set on the form itself.
 *
 * This is important for the form to be managed.
 */
export interface ManagedFormProps {
  /** The form submit handler, use this in `onSubmit` of the enclosing form element. */
  onSubmit: (e?: React.FormEvent<HTMLFormElement> | undefined) => void;
  id?: string;
}

/**
 * Options for getting the submit button of a managed form.
 */
type SubmitButtonOptions = {
  /**
   * Button text.
   */
  text?: string;
  /**
   * If button is disabled when the form is not dirty.
   * Default: `false`
   */
  disableIfNotDirty?: boolean;
  /**
   * If the button is hidden.
   * As the naming suggests, the primary use-case of using this is to enable submitting of the form
   * when pressing the Enter key.
   */
  hideButKeepForSubmitOnEnter?: boolean;
  /**
   * If the button is disabled when the form is not valid.
   * Default is false
   */
  disableIfNotValid?: boolean;

  /**
   * If the button should be disabled (gives the ability to control submit button based on external conditions).
   * Default is false.
   */
  disable?: boolean;

  /**
   * Scale of the submit button.
   */
  buttonScale?: Scale;
};

/**
 * Options for getting the reset button of a managed form.
 */
type ResetButtonOptions = {
  /**
   * Button text.
   */
  text?: string;

  /**
   * Custom onClick function that will be executed before form reset.
   */
  onClick?: () => void;
};

/**
 * Helpers for rapidly and safely creating managed forms.
 *
 * Much of this is passed through from formik with stricter type checks.
 * This gives you access to the form state, errors, whether inputs were touched by the user, etc.
 *
 * Read the documentation of the individual fields for more details.
 *
 */
// eslint-disable-next-line @typescript-eslint/ban-types
export interface ManagedFormHelper<T extends object> {
  /** Whether the form is currently submitting. */
  isSubmitting: boolean;
  /** Whether the form is currently validating. */
  isValidating: boolean;
  /** Whether the form is currently valid. */
  isValid: boolean;
  /** Whether the form validates itself on every user interaction. */
  validateOnBlur: boolean;
  /** Whether the form validates itself on every user input. */
  validateOnChange: boolean;

  /** The initial state, in case you want to render them somewhere else. */
  initialValues: T;
  /**
   * The current form state. **Prefer `getField` to this!**
   *
   * Only use it when the value of a field depends on that of another.
   */
  values: T;
  /**
   * A mapping from each element of the state to an error string, or undefined if it's valid.
   * **Prefer `getField` to this!**
   */
  errors: FormErrors<T>;
  /** A mapping from each element to whether the user touched it. **Prefer `getField` to this!** */
  touched: FormTouched<T>;
  /** Whether the form is currently dirty (as compared to the last submitted values). */
  dirty: boolean;

  /**
   * A helper function that returns all properties that are necessary for an input to be managed by the form.
   *
   * @param property The property of the form state to which this input maps.
   * @returns the {@link ManagedFormFieldProps}
   */
  getField: <Name extends keyof T>(
    property: Name
  ) => ManagedFormFieldProps<T, Name>;
  /** Create a standardized submit button. It's text defaults to 'view.button.submit', but can be overridden if necessary. */
  getSubmitButton: (options?: SubmitButtonOptions) => JSX.Element;
  /** Create a standardized reset button. It's text defaults to 'view.button.reset', but can be overridden if necessary. */
  getResetButton: (options?: ResetButtonOptions) => JSX.Element;

  /** get the properties needed on the surrounding form itself. */
  getFormProps: () => ManagedFormProps;

  /** The number of times onSubmit was called, *before validation*. */
  submitCount: number;

  /** This method can be used in case more than one field should be changed at once so to prevent too many rerenders.
   * Usage:
   *    setValues(state => ({...state, newKey: newValue}))
   */
  setValues: (values: any) => void;

  setFieldTouched: (
    field: string,
    touched?: boolean,
    shouldValidate?: boolean | undefined
  ) => Promise<FormikErrors<FormikValues>> | Promise<void>;
}

/**
 * Formik makes a nested `touched` object because it technically supports nested form state.
 *
 * we do not, and I'm not sure what exactly yup does with nested error messages, so this function simply flattens everything to one level.
 * (since our inputs all work on the first level, even if they represent arrays like e.g. Select)
 *
 * @param params
 * @return
 */
export function flattenTouched(
  params: [string, unknown][]
): [string, boolean][] {
  /**
   * A small function to map an array of something to the type expected by flattenTouched, to make
   * recursive calls easier.
   * @param val the array to map
   */
  function mapArrayVal(val: Array<unknown>): [string, unknown][] {
    return [['', val]];
  }

  return params.map(([key, val]) => {
    // If the value is an array, recurse and then collect (the array is touched if one element was touched)
    if (Array.isArray(val)) {
      return [
        key,
        val
          .map(mapArrayVal)
          .flatMap(flattenTouched)
          .filter(i => i[1]).length > 0
      ];
    }
    // If the value is an object, recurse and collect (the object is touched if one property was touched)
    if (typeof val === 'object' && val !== null && val !== undefined) {
      return [
        key,
        flattenTouched(Object.entries(val)).filter(([, val]) => val).length > 0
      ];
    }
    console.debug(key, val);
    return [key, val as boolean];
  });
}

/**
 * Create a managed form complete with state, validation, dirty tracking etc.
 *
 * This uses functionality from `yup` for validation and `formik` for the core functionality,
 * but is heavily adapted to suit our common components and comes with greatly improved type checking.
 *
 * See the 'Managed Form' story for details.
 *
 * TODO: handle nested state objects: https://www.reddit.com/r/typescript/comments/aynx0o/safe_deep_property_access_in_typescript/ei65qw6/?context=3 https://codewithstyle.info/Deep-property-access-in-TypeScript/
 *
 * @param opts {@link UseFormParam}
 * @return managedForm object that should be used to configure the form, inputs and buttons.
 *
 */
// overload for no validation
// eslint-disable-next-line @typescript-eslint/ban-types
export function useManagedForm<T extends object>(
  opts: UseFormParam<T> & {
    validate?: undefined;
    validationSchema?: undefined;
  }
): ManagedFormHelper<T>;
// overload for yup validation
// eslint-disable-next-line @typescript-eslint/ban-types
export function useManagedForm<T extends object>(
  opts: UseFormParam<T> & {
    validationSchema: Required<UseFormParam<T>>['validationSchema'];
    validate?: undefined;
  }
): ManagedFormHelper<T>;
// overload for manual validation
// eslint-disable-next-line @typescript-eslint/ban-types
export function useManagedForm<T extends object>(
  opts: UseFormParam<T> & {
    validate: Required<UseFormParam<T>>['validate'];
    validationSchema?: undefined;
  }
): ManagedFormHelper<T>;

// implementation
// eslint-disable-next-line @typescript-eslint/ban-types
export function useManagedForm<T extends object>({
  onSubmit,
  autoSubmitWhenValid,
  validateOnBlur = true,
  validate,
  scrollErrorIntoView = false,
  formId,
  ...config
}: UseFormParam<T>): ManagedFormHelper<T> {
  // remember what we last submitted successfully
  const lastSubmittedValue = useRef<T | undefined>(undefined);
  const [wasSubmitting, setWasSubmitting] = useState(false);
  const formik = useFormik<T>({
    ...config,
    validateOnBlur: validateOnBlur,
    validateOnChange: !!autoSubmitWhenValid || config.validateOnChange,
    validate: validate && (values => validate(values, {})),
    onSubmit: (values, formikHelpers) => {
      const result = onSubmit(
        values,
        () => {
          lastSubmittedValue.current = values;
          formikHelpers.setSubmitting(false);
        },
        formikHelpers.resetForm
      );
      if (typeof result === 'object') {
        return result.then(() => (lastSubmittedValue.current = values));
      }
    }
  });
  // dirty normally Returns true if values are not deeply equal from initial values, false otherwise.
  // we are changing its meaning to be "values changed from last submit" here.
  formik.dirty = useMemo(
    () => !isEqual(formik.values, lastSubmittedValue.current),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [formik.values, lastSubmittedValue.current]
  );

  // auto-submit on save if requested
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSubmit = useCallback(
    _.debounce(
      () => formik.submitForm(),
      typeof autoSubmitWhenValid === 'number' ? autoSubmitWhenValid : 500
    ),
    [formik.submitForm, autoSubmitWhenValid]
  );
  useEffect(() => {
    // auto submit dirty form
    if (autoSubmitWhenValid && formik.dirty) debouncedSubmit();
  }, [autoSubmitWhenValid, formik.dirty, debouncedSubmit, formik.values]);

  // auto submit on mount with initialValues.
  useEffect(() => {
    if (autoSubmitWhenValid) debouncedSubmit();
    // cheating the dependencies a little bit to get the behaviour we want
  }, [formik.initialValues]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (scrollErrorIntoView && formId) {
      if (formik.isSubmitting && !wasSubmitting) {
        setWasSubmitting(true);
      }
      if (
        wasSubmitting &&
        !formik.isSubmitting &&
        !formik.isValidating &&
        !formik.isValid
      ) {
        setWasSubmitting(false);

        const form = document.querySelector(`#${formId}`);
        if (form) {
          const errorMessages = Array.from(
            form.querySelectorAll('.error-message')
          );

          const openCollapsiblesWithErrors = async function () {
            // open collapsibles with errors
            // works with not lazy-rendered collapsible or when collapsible was expanded so DOM contains error messages

            const getCollapsedParentCollapsibles = function (
              errMsg: Element | null
            ) {
              // get collapsed collapsibles that are above passed error message and are within the form
              const collapsibles = [];
              let collapsible = errMsg?.closest(
                '.ehs-collapsible:not(.expanded)'
              );
              while (collapsible && form.contains(collapsible)) {
                // so to open starting from the top one
                collapsibles.unshift(collapsible);
                collapsible = collapsible?.parentElement?.closest(
                  '.ehs-collapsible:not(.expanded)'
                );
              }
              return collapsibles;
            };

            new Set(
              errorMessages.flatMap(getCollapsedParentCollapsibles)
            ).forEach(collapsible =>
              (collapsible?.querySelector(
                '.trigger'
              ) as HTMLButtonElement)?.click()
            );
          };

          openCollapsiblesWithErrors().then(() => {
            const elementToScrollIn = errorMessages[0]?.parentElement;

            elementToScrollIn?.scrollIntoView({
              behavior: 'smooth'
            });

            elementToScrollIn
              ?.querySelector('input')
              ?.focus({ preventScroll: true });
          });
        } else {
          console.warn(
            `Form field with invalid input cannot be scrolled into view: no form with id '${formId}' was found`
          );
        }
      }
    }
  }, [
    formId,
    formik.errors,
    formik.isSubmitting,
    formik.isValid,
    formik.isValidating,
    scrollErrorIntoView,
    wasSubmitting
  ]);
  // Since we only want to allow form fields to work on the first level, flatten the touched object.
  const flatTouched: FormTouched<T> = Object.fromEntries(
    flattenTouched(Object.entries(formik.touched))
  ) as FormTouched<T>;

  // Helper to directly set all field props
  const getField = <Name extends keyof T>(
    name: Name
  ): ManagedFormFieldProps<T, Name> => ({
    onChange: (value: T[Name]) => {
      formik.handleChange(({
        target: {
          name: name,
          value: value
        }
      } as unknown) as ChangeEvent);
    },
    onBlur: () =>
      formik.handleBlur({
        target: {
          name: name
        }
      }),

    name: name,
    value: formik.values[name],
    error:
      formik.errors[name] && (autoSubmitWhenValid || flatTouched[name])
        ? (formik.errors[name] as string)
        : undefined,
    touched: flatTouched[name]
  });

  const getSubmitButton = (options?: SubmitButtonOptions): JSX.Element => (
    <Button
      variant="primary"
      type="submit"
      style={
        options?.hideButKeepForSubmitOnEnter ? { display: 'none' } : undefined
      }
      scale={options?.buttonScale}
      disabled={
        (options?.disable ?? false) ||
        formik.isSubmitting ||
        ((options?.disableIfNotValid ?? false) && !formik.isValid) ||
        ((options?.disableIfNotDirty ?? false) && !formik.dirty)
      }
      text={options?.text ?? intl('view.button.submit')}
      form={formId ?? undefined}
    />
  );

  const getResetButton = (options: ResetButtonOptions): JSX.Element => (
    <Button
      variant="gray"
      type="reset"
      disabled={formik.isSubmitting}
      onClick={() => {
        options?.onClick?.();
        formik.handleReset(undefined);
      }}
      text={options?.text ?? intl('view.button.reset')}
    />
  );

  return {
    isSubmitting: formik.isSubmitting,
    isValidating: formik.isValidating,
    isValid: formik.isValid,
    validateOnBlur: formik.validateOnBlur,
    validateOnChange: formik.validateOnChange,

    initialValues: formik.initialValues,

    values: formik.values,
    setValues: values => {
      const setValuesPromise: Promise<any> = formik.setValues(
        state => ({ ...state, ...values }),
        false
      );
      setValuesPromise.then((res: any) => {
        const touchedFields = Object.assign(
          {},
          ...Object.keys(values).map((k: string) => ({ [k]: true }))
        );
        formik.setTouched(
          { ...flatTouched, ...touchedFields },
          formik.validateOnChange || formik.validateOnBlur
        );
      });
    },
    setFieldTouched: formik.setFieldTouched,
    errors: formik.errors,
    touched: flatTouched,
    dirty: formik.dirty,
    getField,
    getSubmitButton,
    getResetButton,

    getFormProps: () => ({
      onSubmit: formik.handleSubmit,
      id: formId
    }),
    submitCount: formik.submitCount
  } as ManagedFormHelper<T>;
}
